<script setup lang="ts">
// Imports
import { ref, onMounted, onBeforeUnmount } from 'vue';
import Hls from 'hls.js';
import { getMuxThumbnail } from '~/utils/GetMuxThumbnail';

//   Define the props
const props = withDefaults(
  defineProps<{
    muxPlaybackId: string;
    muxStreamingUrl: string;
    autoplay?: boolean;
    thumbnailWidth?: number;
    thumbnailTime?: number;
  }>(),
  {
    autoplay: false,
    thumbnailWidth: 1000,
    thumbnailTime: 0,
  },
);

// Define variables
let hls: Hls | null = null;

// Define refs
const videoElementRef = ref<HTMLVideoElement | null>(null);
const isReady = ref(false);

// If autoplay is true, the video will be muted
const muted = computed(() => {
  return props.autoplay ? true : false;
});

//
const loop = computed(() => {
  return props.autoplay ? true : false;
});

//   Play the video
const play = () => {
  if (isReady.value && videoElementRef.value) {
    videoElementRef.value.play();
  }
};

//   Pause the video
const pause = () => {
  if (videoElementRef.value) {
    videoElementRef.value.pause();
  }
};

//   Get the thumbnail of the video
const getThumbnail = () => {
  return getMuxThumbnail(props.muxPlaybackId, {
    width: props.thumbnailWidth,
    time: props.thumbnailTime,
  });
};

// Expose the play, pause, and isReady methods to the parent component
defineExpose({
  play,
  pause,
  isReady,
});

onMounted(() => {
  const videoElement = videoElementRef.value;

  //   Check if the video element is ready
  if (!videoElement) return;

  //   Check if the video can play the stream
  const onCanPlay = () => {
    isReady.value = true;
    videoElement.removeEventListener('canplay', onCanPlay);
  };

  videoElement.addEventListener('canplay', onCanPlay);

  if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
    // Set the source directly for native HLS support
    videoElement.src = props.muxStreamingUrl;
  } else if (Hls.isSupported()) {
    // Set the source for HLS.js fallback
    hls = new Hls();
    hls.loadSource(props.muxStreamingUrl);
    hls.attachMedia(videoElement);
  } else {
    console.error('HLS is not supported in this browser.');
  }
});

onBeforeUnmount(() => {
  if (hls) {
    hls.destroy();
  }
});
</script>

<template>
  <video
    class="stream-player"
    ref="videoElementRef"
    :poster="getThumbnail()"
    playsinline
    :muted="muted"
    :autoplay="autoplay"
    :loop="loop"
    preload="auto"
  ></video>
</template>

<style lang="scss" scoped>
.stream-player {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
